<template>
  <div
    class="pdp pdp-ad-variant pdp__container container"
    :class="storeConfig.code?.substring(0, 3)"
  >
    <AppBreadcrumbs />
    <div class="pdp__layout">
      <div class="pdp__product">
        <div class="pdp__header">
          <h1 class="pdp__heading heading heading--5">
            {{ esProduct?.name }}
          </h1>

          <ProductReviewsScore
            class="pdp__score"
            :class="{ 'pdp__score--no-reviews': !m2Product.reviewStats.count }"
            :stats="m2Product.reviewStats"
            :show-count="false"
          />
        </div>

        <div
          ref="addToCart"
          class="pdp__info"
        >
          <ProductAddToCart
            class="pdp__addtocart"
            :class="{ 'pdp__addtocart--no-sticky': makeAddToCartNonSticky }"
            :product="m2Product"
            :ad-variant="true"
          >
            <template #below-promise>
              <ProductRelatedColorProducts
                class="add-to-cart__colors"
                :product="esProduct"
                :only-link="true"
              />

              <AppLink
                v-if="esProduct?.url"
                class="add-to-cart__product"
                :href="esProduct.url"
              >
                {{ $t('More about this article') }}
              </AppLink>
            </template>
          </ProductAddToCart>

          <div
            ref="addToCartTrigger"
            class="pdp__addtocart-trigger"
          />
        </div>
      </div>

      <div class="pdp__media">
        <ProductLabelBrand :product="esProduct" />
        <div class="pdp__labels">
          <ProductLabelMarketing :product="esProduct" />
        </div>

        <UiMediaGallery
          v-if="productMediaGallery"
          :name="`pdp-${esProduct.sku}`"
          :options="{
            behavior: 'smooth',
            dots: false,
            fullscreen: true,
            fullscreenOptions: {
              dots: false,
              navigation: true,
              thumbs: true,
              thumbsDirection: 'vertical',
              thumbsTrigger: 'click',
              XofY: true,
            },
            media: productMediaGallery,
            navigation: true,
            thumbs: true,
            thumbsDirection: 'vertical',
            thumbsTrigger: 'mouseover',
            XofY: false,
            imageSize: esProduct?.imageSize || 'M standaard',
          }"
        />
        <div
          id="videoly-videobox-placeholder"
          class="pdp__videoly"
        />
      </div>

      <div class="pdp__listing">
        <h2 class="pdp__listing-title heading heading--1">
          {{ leafCategory?.name }}
        </h2>

        <ProductListing
          :product="esProduct"
        />
      </div>

      <div class="pdp__extra-data">
        <div
          id="product-reviews"
          class="pdp__reviews "
        >
          <LazyUiDialog
            v-if="m2Product.reviewStats.count"
            name="modal-reviews"
            class="modal-reviews"
            dialog-type="responsive"
          >
            <template #dialogHeader>
              {{ $t('Reviews') }}
            </template>

            <ProductReviews :product="m2Product" />
          </LazyUiDialog>

          <LazyUiDialog
            class="modal-review-create"
            name="modal-review-create"
            dialog-type="responsive"
          >
            <template #dialogHeader>
              <span
                v-if="reviewCreateSuccess"
                v-text="$t('Thank you for adding review for', { product: esProduct.name })"
              />
              <span
                v-else
                v-text="$t('Create your review for', { product: esProduct.name })"
              />
            </template>
            <p v-if="reviewCreateSuccess">
              {{ $t('It takes up to 24 hours') }}
            </p>

            <ClientOnly v-else>
              <LazyFormReviewCreate

                :product="esProduct"
                @success="(val) => reviewCreateSuccess = val"
              />
            </ClientOnly>
          </LazyUiDialog>
        </div>

        <!--  -->
        <!--  -->
        <!--  -->
        <!--  -->
        <!--  -->
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const route = useRoute()
const meta = inject('meta', ref())
const { t } = useI18n({ useScope: 'global' })
const { storeConfig } = useStore()

const { public: config, app } = useRuntimeConfig()
const [_scheme, domain] = config.baseDomain.split('://')

const {
  m2Product,
  esProduct,
  parentCategories,
  fetchProductData,
} = usePDP(meta)

await fetchProductData()

provide('esProduct', esProduct)

const leafCategory = computed(() => {
  const categories = (esProduct.value?.categories ?? []) as Array<{
    id: string
    level: number
    name: string
    parentId: string
    path: string
  }>

  return [...categories].sort((a, b) => b.level - a.level)[0] ?? null
})

const modalReviewCreate = useUiDialog('modal-review-create')

const reviewCreateSuccess = ref(false)
const addToCartTrigger = ref(null)
const makeAddToCartNonSticky = ref(false)

// const addToCart = ref<HTMLElement | null>(null)
// const addToCartIsVisible = useElementVisibility(addToCart)

const productMediaGallery = computed(() => {
  return (m2Product?.value?.media_gallery || []).toSorted((a, b) => {
    return a.position - b.position
  }).map((media: any) => {
    return {
      alt: media.label,
      src: media.url,
    }
  })
})

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: esProduct?.value?.name,
  parents: [...parentCategories.value].sort((a, b) => a.level - b.level),
})

const { pageView, viewItem } = useDataLayer()
pageView('detail', esProduct?.value?.name)
viewItem(
  esProduct?.value,
  { skus: m2Product.value?.variants?.map((variant: any) => variant.product.sku) || [esProduct.value?.sku] },
)

const { getCompareProductListData, setRootCategory } = useCompareProductList()

onMounted(() => {
  if (route.query.review === null) {
    nextTick(() => {
      modalReviewCreate.open()
    })
  }

  const {
    url: parentCategoryUrl,
    label: parentCategoryLabel,
  } = [...parentCategories.value].sort((a, b) => a.level - b.level)[0] || {}

  setRootCategory({
    url: parentCategoryUrl,
    name: parentCategoryLabel,
  })

  getCompareProductListData()

  // Setup intersection observer for add to cart trigger
  nextTick(() => {
    if (addToCartTrigger.value) {
      const observer = new IntersectionObserver(
        (entries) => {
          makeAddToCartNonSticky.value = entries[0]?.isIntersecting ?? false
        },
        {
          threshold: 0,
          rootMargin: '0px 0px -72px 0px',
        },
      )
      observer.observe(addToCartTrigger.value)

      onUnmounted(() => observer.disconnect())
    }
    else {
      // console.log('addToCartTrigger not found')
    }
  })
})

const metaTitle = computed(
  () => esProduct?.value?.name + ' ' + esProduct?.value?.sku,
)

const metaDescription = computed(
  () => t(`pdp.metaDescription`, {
    name: esProduct?.value?.name,
    sku: esProduct?.value?.sku,
    domain: domain.replace('www.', ''),
  }),
)

useHead({
  title: metaTitle,
  meta: [
    { hid: 'description', name: 'description', content: metaDescription },
    { hid: 'robots', name: 'robots', content: 'index, follow' },
  ],
})

const { canonical } = useSeo()
canonical(computed(() => esProduct.value?.url))

const productSnippet = () => {
  const { locale, locales } = useI18n({ useScope: 'global' })
  const currency = locales.value.find(lcl => lcl.code === locale.value)?.numberFormats.currency.currency || 'EUR'
  const reviewStats = m2Product.value?.reviewStats?.count ? m2Product.value?.reviewStats : null
  const aggregateRating = reviewStats
    ? {
        '@type': 'AggregateRating',
        'ratingValue': reviewStats?.rating,
        'reviewCount': reviewStats?.count,
      }
    : undefined

  useHead({
    script: [
      {
        'hid': 'Product',
        'type': 'application/ld+json',
        'data-nosnippet': true,
        'innerHTML': {
          '@context': 'https://schema.org',
          '@type': 'Product',
          'name': `${esProduct.value?.name} ${esProduct.value?.descriptiveName}`,
          'image': `${config.xcdnUrl}OQ75_RM830,1066,255,255,255/-/catalog/product${esProduct.value?.image}`,
          // 'description': this.metaDescription,
          'sku': esProduct.value?.sku,
          'brand': {
            '@type': 'Brand',
            'name': esProduct.value?.brand?.label,
          },
          'offers': {
            '@type': 'Offer',
            'url': `${config.baseDomain}${app.baseURL}${esProduct.value?.url}`.replace(/\/+/g, '/').replace(':/', '://'), // Safari compatible version
            'priceCurrency': currency,
            'price': esProduct.value?.finalPrice || 0,
            'itemCondition': 'https://schema.org/NewCondition',
            'availability': `https://schema.org/${m2Product.value?.stock_status === 'OUT_OF_STOCK' ? 'OutOfStock' : 'InStock'}`,
          },
          // ...{ review: reviews },
          ...{ aggregateRating: aggregateRating },
        },
        'tagPosition': 'bodyClose',
      },
    ],
  })
}

productSnippet()

// const { recentlyViewed } = useRecentlyViewed(esProduct?.value?.sku)
</script>

<style>
  @import url("~layers/app/pages/~pdp-ad-variant.css");
</style>
